import { type DisplayStatusType, type CountryCode, type DeliveryTimeSlot, type ProjectCode } from '@constants'
import { type Nullable } from '../nullable'
import { type DiscountApplicationMethod, type DiscountItem } from './coupon'
import { type RefundStatus } from './eCommerceOrders'
import { type ReceiptType } from './eCommercePayments'
import { type PaymentMethod } from './payment'
import { type TrackingLabelDetails } from './shipments-details'
import { type CvsStoreInfo, type DeliveryClass, type ShippingMethod, type ShippingMethodType } from './shipping'
import { type PaymentSubshipInfo } from './subship'

export enum OrderItemType {
  PRODUCT = 'product',
}

export enum FastpassOrderStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELED = 'cancelled',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}
export enum FastpassChildOrderStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  PROCESSING = 'processing',
  READY_FOR_SHIPMENT = 'ready-for-shipment',
  CANCELLED = 'cancelled',
}

export enum ServiceType {
  TAPPAY = 'tappay',
  ECPAY = 'ecpay',
  CHECKOUTPAY = 'checkoutpay',
  LINEPAY = 'linepay',
}

export enum FastpassOrderInvoiceReceiptType {
  NON_BUSINESS_EINVOICE = 'non_business_einvoice',
  BUSINESS_EINVOICE = 'business_einvoice',
  PHONE_CARRIER = 'phone_carrier',
  DONATE_INVOICE = 'donate_invoice',
  CHARITY_ORGANIZATION = 'charity_organization',
}

export enum FastpassOrderInvoiceState {
  ISSUED = 'issued',
  VOIDED = 'voided',
}

export interface Transaction {
  /** 交易編號 */
  id: string
  /** 交易訂單加密雜湊值 */
  order_hash: string
  customer_id: Nullable<string>
  /** 交易訂單識別碼 */
  transaction_id: string
  /** 國家代碼 */
  country_code: CountryCode
  /** 交易狀態 */
  status: FastpassOrderStatus
  /** 訂單建立時間 */
  created_at: string
  /** 幣別 */
  currency: string
  /** 商品合計 */
  subtotal: number
  /** 稅金 */
  tax: number
  /** 優惠合計 */
  discount_total: number
  /** 運費合計 */
  shipping_total: number
  /** 運費小計 */
  shipping_subtotal: number
  /** 優惠後總額 */
  total_after_discount: 0
  /** 運費後總額 */
  total_after_shipping: 0
  /** 訂購總額 */
  total: number
  /** 點數折扣 */
  reward_point_discount: number
  /** 推薦碼折扣 */
  referral_code_discount: number
  /** 折扣項目 */
  discount_items: DiscountItem[]
  payment_method: PaymentMethod
  /** 可用付款方式 */
  payment_methods: PaymentMethod[]
  /** 付款資訊(目前只有ecpay_atm會有) */
  payment_info: FpPaymentInfo | null
  /** 子訂單資訊 */
  child_orders: ChildOrder[]
  /** 滿額禮是否已確認 */
  item_confirmed: boolean
  /** 跳轉至第三方支付的 URL */
  payment_redirect_url: string
  /** 滿額禮是否要宅配寄送 */
  is_giveaway_dispatch: boolean
  /** 客戶備註 */
  customer_note: string
  /** 是否需要訂單明細 PDF */
  need_pdf: boolean
  /** 是否為跨月帳款 */
  posting_date_in_next_month: boolean
  /** 是否需要退款聲明 */
  refund_statement: boolean
  /** 預計配送日期 */
  delivery_date: string
  /** 配送時段 */
  delivery_time_slot: DeliveryTimeSlot
  /** 收件人姓名 */
  receiver_name: string
  /** 收件人電話 */
  receiver_phone: string
  /** 收件人信箱 */
  receiver_email: string
  /** 配送地址 */
  shipping_address: string
  shipping_address_1: string
  shipping_address_2: string
  shipping_address_postcode: string
  shipping_address_city: string
  shipping_address_state: string
  shipping_method: ShippingMethod
  applied_shipping_method_id: number
  /** 發票類型 */
  receipt_type: FastpassOrderInvoiceReceiptType
  /** 發票選項 */
  invoice_option: InvoiceOption

  payment_secret: string
  /** 採購單名稱 */
  purchase_order_name: string
  cvs_info: CvsStoreInfo
  /** 是否有線下滿額禮或贈品 */
  has_offline_giveaway: boolean
  /** 加價購總金額 */
  addon_v2_total: number
  /** 加價購總金額（未折扣） */
  addon_v2_total_before_discount: number
  /** 退款狀態 */
  refund_status: RefundStatus | null
}

export interface DiscountInfoGift {
  sku: string
  project_code: ProjectCode
  display_name: string
  stock: number
  is_out_of_stock: boolean
  thumbnail: string
}

export interface InvoiceOption {
  business_ubn_title: string
  business_ubn: string
  phone_carrier_number: string
  charity_organization: string
  refund_statement: boolean
  type: ReceiptType
}

export interface MerchantProfile {
  shipping_address_first_name: string
  shipping_address_email: string
  shipping_address_phone: string
}

export interface OrderInvoice {
  invoice_number: string
  receipt_type: FastpassOrderInvoiceReceiptType
  business_ubn_title: string
  business_ubn: string
  refund_statement: boolean
}

export interface TrackingLabel {
  id: number
  tracking_no: string
  /** 物流商代碼，如 'ezcat' */
  provider: string
  /** 物流商名稱，如 '黑貓' */
  provider_name: string
  tracking_status: string | null
  tracking_status_name: string | null
  tracking_url: string
  tracking_completed: boolean
  tracking_polled_at: string | null
}

export interface Shipment {
  id: number
  shipment_no: string
  seq_no: number | null
  tracking_labels?: TrackingLabelDetails[] | null
}
export interface OrderItem {
  id: number
  product_name: string
  quantity: number
  unit_price: number
  subtotal: number
  total: number
  sku: string
  thumbnail_url: string
  /** 2B 站台顯示用的商品圖，取自 gallery 最後一張 */
  last_gallery_image?: string
  original_price: number | null
  attributes: { key: string, value: string }[]
  sale_price: number | null
  delivery_class: DeliveryClass
  product_limited?: Nullable<{
    life_limit: number
    purchased_count: number
  }>
  order_item_type: string
  variation_id?: number
  product_id: number
  /** 推薦商品（原加價購） */
  is_addon: boolean
  /** 加價購V2 */
  is_addon_v2: boolean
  /** 滿額禮 or 贈品 */
  is_giveaway: boolean
  /** 滿額禮 */
  is_amount_giveaway: boolean
  is_from_point_exchange?: boolean
  /** 信用卡贈品 */
  credit_card_affiliate_code: string
  /** 後端已經將數量呈上特價金額 */
  addon_v2_price: number
  /** 虛擬商品 */
  is_virtual: boolean
  parent_product_id: Nullable<number>
  /** 主商品 id */
  addon_main_product_id: Nullable<number>
  /** 出貨群組編號 */
  group_no: Nullable<number>
  /** 該品項的託運單明細 */
  tracking_labels?: TrackingLabelDetails[] | null
}

export interface ChildOrder {
  /** Order ID */
  id: number
  /** 訂單編號 */
  order_id: string
  /** 品牌 */
  project_code: ProjectCode
  /** 訂單狀態 */
  status: FastpassChildOrderStatus
  /** 顯示用訂單狀態（後端可能未回傳） */
  display_status?: DisplayStatusType
  /** 運送方式 */
  shipping_method: ShippingMethodType
  /** 收件人 */
  shipping_address_first_name: string
  /** 聯絡電話 */
  shipping_address_phone: string
  /** 收貨時間 */
  delivery_time_slot: DeliveryTimeSlot
  /** 預計到貨日 */
  delivery_date: string
  /** 運送類別 */
  delivery_class: DeliveryClass
  /** 額外優惠或費用 */
  extra_fee: number
  /** 稅額 */
  tax: number | null
  /** 小計未折扣總額 */
  subtotal: number
  /** 訂單 PDF 網址 */
  order_pdf_url: string | null
  discount_items: DiscountItem[]
  merchant_profile: {
    shipping_address_first_name: string
    shipping_address_email: string
    shipping_address_phone: string
  }
  order_invoice: {
    state: FastpassOrderInvoiceState
    type: FastpassOrderInvoiceReceiptType
    /** 營業用電子發票 */
    business_ubn_title: string
    /** 營業用電子發票 */
    business_ubn: string
    /** 手機條碼 */
    phone_carrier_number: string | null
    /** 捐贈發票 */
    charity_organization: string | null
    /** 發票開立時間 */
    issued_time: string
    /** 發票號碼 */
    invoice_number: string
    /** 是否可以修改發票資訊 */
    modifiable: boolean
    receipt_type: FastpassOrderInvoiceReceiptType
    refund_statement: boolean
  }
  /** 客戶備註 */
  customer_note: string
  /** 是否接受退貨聲明 */
  refund_statement: boolean
  shipments: Shipment[]
  order_items: OrderItem[]
  shipping_label_providers: ShippingLabelProvider[]
  /** 預購訂單 */
  is_pre_order: boolean
  /** 公益訂單 */
  is_donation: boolean
  customer_id: number
  delivery_reason: string
  shipping_method_title: string
  discount_total: number
  shipping_total: number
  total: number
  shipping: {
    id: number
    order_id: number
    sequence_index: number
    status: string
    shipping_date: string
    billing_address_email: string
    billing_address_phone: string
    billing_address_company: string
    billing_address_postcode: string
    billing_address_country: string
    billing_address_city: string
    billing_address_state: string
    billing_address_meta: {
      last_name: string
      first_name: string
      address1: string
      address2: string
    }
    shipping_address_company: string
    shipping_address_postcode: string
    shipping_address_country: string
    shipping_address_city: string
    shipping_address_state: string
    shipping_address_meta: {
      last_name: string
      first_name: string
      address1: string
      address2: string
    }
    created_at: string
    deleted_at: string
    updated_at: string
  }
  subship_info?: PaymentSubshipInfo
}

export interface ShippingLabelProvider {
  provider: string
  tracking_url: string
  shipping_labels: ShippingLabel[]
}

export interface ShippingLabel {
  tracking_no: string
}

export interface FpPaymentInfo {
  bank_code?: string
  bank_name?: string
  account?: string
  expire_date?: string
  merchant_trade_no?: number
  trade_no?: number
  trade_date?: string
  payment_no?: string
  payment_url?: string
  web_link?: string
}

export interface FpOrderGift {
  project_code: ProjectCode
  sku: string
  display_name: string
  stock: number
  is_out_of_stock: boolean
  thumbnail: string
}

export interface FpGiveawayRule {
  project_code: ProjectCode
  count: number
  accumulable?: boolean
  amount: number
  display: string
  type: 'giveaway'
  gifts: FpOrderGift[]
}

export interface PaymentTransactionGiveaway {
  criteria_id: number
  amount: number
  count: number
  display: string
  discount_trigger_method: DiscountApplicationMethod
  gifts: PaymentTransactionGiveawayGift[]
  is_regular_giveaway: boolean
}

export interface PaymentTransactionGiveawayGift {
  project_code: ProjectCode
  sku: string
  display_name: string
  stock: number
  is_out_of_stock: boolean
  thumbnail: string
  is_chosen: boolean
  attributes: string
}
